import { mockOfficeAgent } from '../agents';
import { mockConversations } from '../conversations';
import { makeStreamStart, makeBlockStart, makeBlockEnd, makeBlockComplete, makeStreamEnd, splitTextIntoDeltas } from '../sseEvents';
const RESPONSE_ID = 'resp-long-stream';
const CONV_ID = 'conv-1';
const MD_BLOCK_1_ID = 'blk-long-md-1';
const MD_BLOCK_2_ID = 'blk-long-md-2';
const TABLE_BLOCK_ID = 'blk-long-tbl';
const MD_BLOCK_3_ID = 'blk-long-md-3';
// Long markdown content split across multiple blocks
const markdown1 = "I'll help you analyze the inspection data for the past quarter. Let me start by gathering the relevant information from our system.\n\n" +
    "First, I'm reviewing all completed inspections from January through March. This includes building inspections, fire safety checks, " +
    'and equipment testing across all your facilities. The data shows some interesting patterns that we should discuss.';
const markdown2 = 'Based on my analysis, I found several key insights:\n\n' +
    '1. **Compliance Rate**: Overall compliance has improved by 12% compared to last quarter\n' +
    '2. **Response Time**: Average inspection completion time decreased from 4.2 days to 3.1 days\n' +
    '3. **Critical Issues**: We identified 23 critical deficiencies that require immediate attention\n\n' +
    "Here's a detailed breakdown by building:";
const dataTableBlock = {
    type: 'data_table',
    block_id: TABLE_BLOCK_ID,
    block_index: 2,
    title: 'Inspection Summary by Building',
    columns: [
        { key: 'building', label: 'Building', sortable: true },
        { key: 'total_inspections', label: 'Total Inspections', sortable: true, align: 'right', format: 'number' },
        { key: 'completed', label: 'Completed', sortable: true, align: 'right', format: 'number' },
        { key: 'pending', label: 'Pending', sortable: true, align: 'right', format: 'number' },
        { key: 'compliance_rate', label: 'Compliance %', sortable: true, align: 'right', format: 'number' }
    ],
    rows: [
        { building: 'Main Office - Building A', total_inspections: 45, completed: 42, pending: 3, compliance_rate: 93 },
        { building: 'Warehouse North', total_inspections: 38, completed: 35, pending: 3, compliance_rate: 92 },
        { building: 'Manufacturing Plant', total_inspections: 52, completed: 48, pending: 4, compliance_rate: 92 },
        { building: 'Distribution Center', total_inspections: 29, completed: 27, pending: 2, compliance_rate: 93 },
        { building: 'Administration Annex', total_inspections: 18, completed: 18, pending: 0, compliance_rate: 100 }
    ],
    total_rows: 5,
    displayed_rows: 5,
    export_options: ['csv', 'excel']
};
const markdown3 = '### Next Steps and Recommendations\n\n' +
    'Based on this analysis, I recommend the following actions:\n\n' +
    '1. **Priority 1 - Critical Deficiencies**: Schedule immediate repairs for the 23 critical issues identified. ' +
    'Most of these are concentrated in the Manufacturing Plant (14 items) and require contractor coordination.\n\n' +
    '2. **Priority 2 - Pending Inspections**: Complete the 12 pending inspections within the next two weeks. ' +
    "I can help schedule these if you'd like.\n\n" +
    '3. **Process Improvement**: Consider implementing automated reminders for recurring inspections. ' +
    'This could improve our already strong completion rate even further.\n\n' +
    '4. **Training**: The Administration Annex achieved 100% compliance - we should document their best practices ' +
    'and share them with other facilities.\n\n' +
    'Would you like me to create action items for any of these recommendations, or would you prefer to dive deeper ' +
    'into any specific area? I can also generate detailed reports for management review.';
// Create a long SSE sequence with intentional delays between events
// This gives users time to click "stop" during streaming
const sseSequence = [
    makeStreamStart(RESPONSE_ID, CONV_ID, mockOfficeAgent),
    // Block 1: Introduction (long markdown)
    makeBlockStart(MD_BLOCK_1_ID, 0, 'markdown'),
    ...splitTextIntoDeltas(MD_BLOCK_1_ID, markdown1, 15), // Split into ~15 character chunks
    makeBlockEnd(MD_BLOCK_1_ID),
    // Block 2: Analysis overview (long markdown)
    makeBlockStart(MD_BLOCK_2_ID, 1, 'markdown'),
    ...splitTextIntoDeltas(MD_BLOCK_2_ID, markdown2, 12),
    makeBlockEnd(MD_BLOCK_2_ID),
    // Block 3: Data table (atomic)
    makeBlockComplete(dataTableBlock, 200),
    // Block 4: Recommendations (very long markdown)
    makeBlockStart(MD_BLOCK_3_ID, 3, 'markdown'),
    ...splitTextIntoDeltas(MD_BLOCK_3_ID, markdown3, 10),
    makeBlockEnd(MD_BLOCK_3_ID),
    makeStreamEnd(RESPONSE_ID, 1, mockOfficeAgent)
];
export const scenario = {
    name: 'Office Assistant — Long Streaming Response',
    description: 'Extended streaming response with multiple blocks (3 markdown + 1 data table). ' +
        'Use this story to test the onStop functionality by clicking the stop button during streaming. ' +
        'The response is intentionally long to provide ample time to test cancellation.',
    conversations: mockConversations,
    userMessage: 'Give me a detailed analysis of our inspection data for the last quarter',
    agentIdentity: mockOfficeAgent,
    sseSequence
};
